/**
 * Funnel Analytics API Client
 * E007-S025 - Conversion Funnel Analytics
 */

import { apiClient } from './apiClient';

export interface FunnelStage {
  name: string;
  count: number;
  rate: number;
  drop_off: number;
}

export interface FunnelData {
  stages: FunnelStage[];
  overall_conversion: number;
  total_revenue: number;
  average_order_value: number;
}

export interface FunnelComparison {
  funnel_a: FunnelData;
  funnel_b: FunnelData;
  stage_differences: Array<{
    stage: string;
    funnel_a_count: number;
    funnel_b_count: number;
    diff_count: number;
    diff_pct: number;
    direction: 'up' | 'down' | 'same';
    indicator: string;
  }>;
  overall_differences: {
    conversion_rate: {
      funnel_a: number;
      funnel_b: number;
      diff: number;
    };
    total_revenue: {
      funnel_a: number;
      funnel_b: number;
      diff: number;
    };
    average_order_value: {
      funnel_a: number;
      funnel_b: number;
      diff: number;
    };
  };
}

export interface FunnelParams {
  start_date: string;
  end_date: string;
  campaign_ids?: string;
  platform?: string;
  age_range?: string;
  gender?: string;
  location_country?: string;
}

/**
 * Fetch funnel analytics data
 */
export async function getFunnelData(params: FunnelParams): Promise<FunnelData> {
  const queryParams = new URLSearchParams();
  queryParams.append('start_date', params.start_date);
  queryParams.append('end_date', params.end_date);
  if (params.campaign_ids) queryParams.append('campaign_ids', params.campaign_ids);
  if (params.platform) queryParams.append('platform', params.platform);
  if (params.age_range) queryParams.append('age_range', params.age_range);
  if (params.gender) queryParams.append('gender', params.gender);
  if (params.location_country) queryParams.append('location_country', params.location_country);

  const response = await apiClient.get<FunnelData>(
    `/funnel/analytics/conversion-funnel?${queryParams.toString()}`
  );
  return response.data;
}

/**
 * Compare two funnels side-by-side
 */
export async function compareFunnels(
  paramsA: FunnelParams,
  paramsB: FunnelParams
): Promise<FunnelComparison> {
  const queryParams = new URLSearchParams();
  
  // Funnel A params
  queryParams.append('start_date_a', paramsA.start_date);
  queryParams.append('end_date_a', paramsA.end_date);
  if (paramsA.campaign_ids) queryParams.append('campaign_ids_a', paramsA.campaign_ids);
  if (paramsA.platform) queryParams.append('platform_a', paramsA.platform);
  if (paramsA.age_range) queryParams.append('age_range_a', paramsA.age_range);
  if (paramsA.gender) queryParams.append('gender_a', paramsA.gender);
  if (paramsA.location_country) queryParams.append('location_country_a', paramsA.location_country);
  
  // Funnel B params
  queryParams.append('start_date_b', paramsB.start_date);
  queryParams.append('end_date_b', paramsB.end_date);
  if (paramsB.campaign_ids) queryParams.append('campaign_ids_b', paramsB.campaign_ids);
  if (paramsB.platform) queryParams.append('platform_b', paramsB.platform);
  if (paramsB.age_range) queryParams.append('age_range_b', paramsB.age_range);
  if (paramsB.gender) queryParams.append('gender_b', paramsB.gender);
  if (paramsB.location_country) queryParams.append('location_country_b', paramsB.location_country);

  const response = await apiClient.get<FunnelComparison>(
    `/funnel/analytics/conversion-funnel/compare?${queryParams.toString()}`
  );
  return response.data;
}

/**
 * Export funnel data as CSV
 */
export function exportFunnelCSV(params: FunnelParams): void {
  const queryParams = new URLSearchParams();
  queryParams.append('start_date', params.start_date);
  queryParams.append('end_date', params.end_date);
  if (params.campaign_ids) queryParams.append('campaign_ids', params.campaign_ids);
  if (params.platform) queryParams.append('platform', params.platform);
  if (params.age_range) queryParams.append('age_range', params.age_range);
  if (params.gender) queryParams.append('gender', params.gender);
  if (params.location_country) queryParams.append('location_country', params.location_country);

  const url = `${apiClient.defaults.baseURL}/funnel/analytics/conversion-funnel/export/csv?${queryParams.toString()}`;
  window.open(url, '_blank');
}

/**
 * Export funnel data as PDF
 */
export function exportFunnelPDF(params: FunnelParams): void {
  const queryParams = new URLSearchParams();
  queryParams.append('start_date', params.start_date);
  queryParams.append('end_date', params.end_date);
  if (params.campaign_ids) queryParams.append('campaign_ids', params.campaign_ids);
  if (params.platform) queryParams.append('platform', params.platform);
  if (params.age_range) queryParams.append('age_range', params.age_range);
  if (params.gender) queryParams.append('gender', params.gender);
  if (params.location_country) queryParams.append('location_country', params.location_country);

  const url = `${apiClient.defaults.baseURL}/funnel/analytics/conversion-funnel/export/pdf?${queryParams.toString()}`;
  window.open(url, '_blank');
}
